import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n';
import {
  HardDrive, Zap, Cloud, ArrowRight, Shield, Globe, Database,
  Check, ExternalLink, Sliders, Star,
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';

// ── All plans for comparison (product data, stays in English) ────────────────
const allPlans = [
  { name: 'Web SSD Special',   type: 'SSD',       storage: 10,   price: 1.66,  priceDisplay: '€19,95/yr', pid: 198,  path: '/webhosting/ssd' },
  { name: 'Web SSD Starter',   type: 'SSD',       storage: 4,    price: 1.95,  priceDisplay: '€1,95/mo',  pid: 196,  path: '/webhosting/ssd' },
  { name: 'Web SSD Extreme',   type: 'SSD',       storage: 25,   price: 5.95,  priceDisplay: '€5,95/mo',  pid: 197,  path: '/webhosting/ssd' },
  { name: 'Web NVMe Special',  type: 'NVMe',      storage: 5,    price: 2.08,  priceDisplay: '€25,00/yr', pid: 316,  path: '/webhosting/nvme' },
  { name: 'Web NVMe Starter',  type: 'NVMe',      storage: 5,    price: 2.95,  priceDisplay: '€2,95/mo',  pid: 314,  path: '/webhosting/nvme' },
  { name: 'Web NVMe Extreme',  type: 'NVMe',      storage: 10,   price: 3.95,  priceDisplay: '€3,95/mo',  pid: 315,  path: '/webhosting/nvme' },
  { name: 'Nextcloud 50 GB',   type: 'Nextcloud', storage: 50,   price: 2.49,  priceDisplay: '€2,49/mo',  pid: 513,  path: '/webhosting/nextcloud' },
  { name: 'Nextcloud 500 GB',  type: 'Nextcloud', storage: 500,  price: 5.00,  priceDisplay: '€5,00/mo',  pid: 514,  path: '/webhosting/nextcloud' },
  { name: 'Nextcloud 1000 GB', type: 'Nextcloud', storage: 1000, price: 7.50,  priceDisplay: '€7,50/mo',  pid: 515,  path: '/webhosting/nextcloud' },
  { name: 'Nextcloud 2000 GB', type: 'Nextcloud', storage: 2000, price: 20.00, priceDisplay: '€20,00/mo', pid: 516,  path: '/webhosting/nextcloud' },
];

const needTypes = [
  { id: 'website',   icon: Globe,     preferType: 'SSD',       storageRecommend: 5   },
  { id: 'shop',      icon: Zap,       preferType: 'NVMe',      storageRecommend: 10  },
  { id: 'cloud',     icon: Cloud,     preferType: 'Nextcloud', storageRecommend: 50  },
  { id: 'wordpress', icon: HardDrive, preferType: 'SSD',       storageRecommend: 5   },
];

const storageLabels = [1, 5, 10, 25, 50, 100, 500];

const typeColors = {
  SSD:       'text-blue-400 bg-blue-500/10',
  NVMe:      'text-primary bg-primary/10',
  Nextcloud: 'text-green-400 bg-green-500/10',
};

function getRecommendations(needType, storageGB) {
  const preferType = needType?.preferType || 'SSD';
  const fitting = allPlans
    .filter(p => p.storage >= storageGB)
    .sort((a, b) => a.price - b.price);
  const top = fitting.find(p => p.type === preferType) || fitting[0];
  const runner = fitting.find(p => p.name !== top?.name) || null;
  return [top, runner].filter(Boolean).slice(0, 2);
}

// ── Component ────────────────────────────────────────────────────────────────
export default function WebhostingPage() {
  const { t } = useTranslation();
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [storageGB, setStorageGB] = useState(5);
  const [showResults, setShowResults] = useState(false);

  const recommendations = showResults ? getRecommendations(selectedNeed, storageGB) : [];

  const hostingOptions = [
    {
      titleKey: 'nav.webhostingSSD',
      desc: 'Reliable SSD hosting with Plesk panel, free SSL and DDoS protection.',
      icon: HardDrive,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      price: '1,95',
      href: '/webhosting/ssd',
      features: [t('webhosting.feat_plesk_label'), t('webhosting.feat_ssl_label'), t('webhosting.badge_ddos'), t('webhosting.badge_mysql')],
    },
    {
      titleKey: 'nav.webhostingNVMe',
      desc: 'Lightning-fast NVMe storage – significantly faster than traditional SSDs.',
      icon: Zap,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      price: '2,95',
      href: '/webhosting/nvme',
      features: ['NVMe Storage', t('webhosting.feat_plesk_label'), t('webhosting.feat_ssl_label'), 'Flat Traffic'],
      recommended: true,
    },
    {
      titleKey: 'nav.nextcloud',
      desc: 'Your own private cloud – pre-installed, managed, ready to use immediately.',
      icon: Cloud,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      price: '2,49',
      href: '/webhosting/nextcloud',
      features: ['Pre-installed', 'Mobile Apps', t('webhosting.nc_badge_users'), 'Prepaid'],
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Web Hosting – SSD, NVMe & Nextcloud"
        description="Web hosting from €1.66/month. SSD and NVMe web hosting with Plesk, free SSL, DDoS protection and WordPress installer. Nextcloud hosting from €2.49/month."
        canonical="/webhosting"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Web Hosting Plans',
          url: 'https://deinserverhost.com/webhosting',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'SSD Web Hosting', url: 'https://deinserverhost.com/webhosting/ssd' },
            { '@type': 'ListItem', position: 2, name: 'NVMe Web Hosting', url: 'https://deinserverhost.com/webhosting/nvme' },
            { '@type': 'ListItem', position: 3, name: 'Nextcloud Web Hosting', url: 'https://deinserverhost.com/webhosting/nextcloud' },
          ],
        }}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-gradient">{t('webhosting.title')}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">{t('webhosting.subtitle')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { icon: Shield,   key: 'badge_ddos' },
                { icon: Zap,      key: 'badge_setup' },
                { icon: Globe,    key: 'badge_ssl' },
                { icon: Database, key: 'badge_mysql' },
              ].map(({ icon: Icon, key }) => (
                <div key={key} className="flex items-center gap-2 px-4 py-2 rounded-full glass">
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-sm">{t(`webhosting.${key}`)}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Hosting type cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {hostingOptions.map((option, index) => (
              <motion.div
                key={option.titleKey}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full relative hover:border-primary/50 transition-colors ${option.recommended ? 'border-primary/30 bg-primary/5' : ''}`}>
                  {option.recommended && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">{t('common.recommended')}</Badge>
                  )}
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-xl ${option.bgColor} flex items-center justify-center mb-4`}>
                      <option.icon className={`w-7 h-7 ${option.color}`} />
                    </div>
                    <CardTitle>{t(option.titleKey)}</CardTitle>
                    <CardDescription>{option.desc}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {option.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check className="w-4 h-4 text-green-500 shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-sm text-muted-foreground">{t('common.from')}</span>
                      <span className="text-3xl font-black text-primary">€{option.price}</span>
                      <span className="text-muted-foreground">{t('common.perMonth')}</span>
                    </div>
                    <Link to={option.href}>
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        data-testid={`hosting-card-${option.href.split('/').pop()}`}
                      >
                        {t('common.viewPlans')} <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* ── Price Comparison Calculator ───────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-16"
          >
            <div className="glass rounded-2xl border border-border overflow-hidden">
              <div className="p-6 border-b border-border flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sliders className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{t('webhosting.calc_title')}</h2>
                  <p className="text-sm text-muted-foreground">{t('webhosting.calc_subtitle')}</p>
                </div>
              </div>

              <div className="p-6">
                {/* Step 1 */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">1</div>
                    <span className="font-semibold">{t('webhosting.calc_step1')}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {needTypes.map((opt) => {
                      const Icon = opt.icon;
                      const isSelected = selectedNeed?.id === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => { setSelectedNeed(opt); setShowResults(false); }}
                          data-testid={`calc-need-${opt.id}`}
                          className={`p-4 rounded-xl border text-left transition-all duration-200 ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border hover:border-primary/40 hover:bg-primary/5'
                          }`}
                        >
                          <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div className="text-sm font-medium">{t(`webhosting.calc_need_${opt.id}`)}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Step 2 */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">2</div>
                    <span className="font-semibold">{t('webhosting.calc_step2')}</span>
                    <span className="ml-auto text-primary font-bold">
                      {storageGB >= 1000 ? `${storageGB / 1000} TB` : `${storageGB} GB`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={storageLabels.length - 1}
                    value={storageLabels.indexOf(storageGB) === -1 ? 1 : storageLabels.indexOf(storageGB)}
                    onChange={(e) => { setStorageGB(storageLabels[Number(e.target.value)]); setShowResults(false); }}
                    className="w-full accent-primary h-2 cursor-pointer"
                    data-testid="storage-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    {storageLabels.map(v => (
                      <span key={v}>{v >= 1000 ? `${v / 1000}TB` : `${v}GB`}</span>
                    ))}
                  </div>
                </div>

                {/* Calculate button */}
                <Button
                  onClick={() => setShowResults(true)}
                  disabled={!selectedNeed}
                  className="w-full bg-primary hover:bg-primary/90 font-bold"
                  size="lg"
                  data-testid="calc-submit-btn"
                >
                  <Star className="w-4 h-4 mr-2" />
                  {t('webhosting.calc_calculate')}
                </Button>

                {/* Results */}
                <AnimatePresence>
                  {showResults && recommendations.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="mt-8"
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <Check className="w-5 h-5 text-green-500" />
                        <span className="font-semibold">{t('webhosting.calc_results')}</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {recommendations.map((plan, i) => (
                          <motion.div
                            key={plan.name}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.08 }}
                            className={`p-5 rounded-xl border ${i === 0 ? 'border-primary/60 bg-primary/5' : 'border-border'}`}
                            data-testid={`calc-result-${i}`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                {i === 0 && <Badge className="bg-primary text-xs mb-1">{t('webhosting.calc_bestChoice')}</Badge>}
                                <div className="font-bold">{plan.name}</div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[plan.type]}`}>
                                  {plan.type}
                                </span>
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-black text-primary">{plan.priceDisplay}</div>
                              </div>
                            </div>
                            <div className="text-sm text-muted-foreground mb-4">
                              {plan.storage >= 1000 ? `${plan.storage / 1000} TB` : `${plan.storage} GB`} Storage
                            </div>
                            <Link to={plan.path}>
                              <Button
                                size="sm"
                                className={`w-full ${i === 0 ? 'bg-primary hover:bg-primary/90' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
                              >
                                {t('webhosting.calc_viewPlan')} <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center p-8 glass rounded-2xl"
          >
            <h2 className="text-2xl font-bold mb-4">{t('webhosting.notSureTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">{t('webhosting.notSureDesc')}</p>
            <Link to="/product-finder">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover">
                {t('common.startProductFinder')}
              </Button>
            </Link>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
